import React, { useState } from 'react';
import { getSlotLabel, getPalletLabel, reprintPalletLabel, getBoxLabel, getAllBoxLabels, getCompanionBoard, getChallanPDF } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Tag, MapPin, Package, Box, Printer, FileText, Grid } from 'lucide-react';
import { SlotPicker } from '@/components/SlotPicker';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function openLabel(url) {
  window.open(`${API_BASE}${url}`, '_blank');
}

export default function LabelsPage() {
  const [slotAddr, setSlotAddr] = useState('');
  const [palletRef, setPalletRef] = useState('');
  const [boxRef, setBoxRef] = useState('');
  const [rackCode, setRackCode] = useState('');
  const [challanRef, setChallanRef] = useState('');
  const [loading, setLoading] = useState('');

  const handleSlotLabel = async () => {
    if (!slotAddr) return toast.error('Enter slot address');
    setLoading('slot');
    try {
      const res = await getSlotLabel(slotAddr.toUpperCase());
      openLabel(res.data.url);
      toast.success('Slot label generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(''); }
  };

  const handlePalletLabel = async () => {
    if (!palletRef) return toast.error('Enter pallet ref');
    setLoading('pallet');
    try {
      const res = await getPalletLabel(palletRef.toUpperCase());
      openLabel(res.data.url);
      toast.success(`Pallet label v${res.data.label_version} generated`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(''); }
  };

  const handleReprintPallet = async () => {
    if (!palletRef) return toast.error('Enter pallet ref');
    setLoading('reprint');
    try {
      const res = await reprintPalletLabel(palletRef.toUpperCase());
      openLabel(res.data.url);
      toast.success(`Reprinted as Label v${res.data.label_version}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(''); }
  };

  const handleAllBoxLabels = async () => {
    if (!palletRef) return toast.error('Enter pallet ref');
    setLoading('boxes');
    try {
      const res = await getAllBoxLabels(palletRef.toUpperCase());
      if (res.data.labels?.length > 0) {
        res.data.labels.forEach(l => openLabel(l.url));
        toast.success(`${res.data.labels.length} box labels generated`);
      } else {
        toast.error('No boxes found');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(''); }
  };

  const handleCompanionBoard = async () => {
    if (!rackCode) return toast.error('Enter rack code');
    setLoading('companion');
    try {
      const res = await getCompanionBoard(rackCode.toUpperCase());
      openLabel(res.data.url);
      toast.success('Companion board generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(''); }
  };

  const handleChallanPDF = async () => {
    if (!challanRef) return toast.error('Enter challan ref');
    setLoading('challan');
    try {
      const res = await getChallanPDF(challanRef.toUpperCase());
      openLabel(res.data.url);
      toast.success('Challan PDF generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(''); }
  };

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="labels-page">
      <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
        <Tag size={20} /> Label Generator
      </h1>

      <Tabs defaultValue="slot">
        <TabsList className="w-full grid grid-cols-5">
          <TabsTrigger value="slot" className="text-xs"><MapPin size={12} className="mr-1" /> Slot</TabsTrigger>
          <TabsTrigger value="pallet" className="text-xs"><Package size={12} className="mr-1" /> Pallet</TabsTrigger>
          <TabsTrigger value="box" className="text-xs"><Box size={12} className="mr-1" /> Box</TabsTrigger>
          <TabsTrigger value="companion" className="text-xs"><Grid size={12} className="mr-1" /> Board</TabsTrigger>
          <TabsTrigger value="challan" className="text-xs"><FileText size={12} className="mr-1" /> Challan</TabsTrigger>
        </TabsList>

        {/* Slot Label */}
        <TabsContent value="slot">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Slot Label (150x150mm)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">Row colour stripe, slot address large bold, QR code 100x100mm</p>
              <div>
                <Label className="text-xs">Slot Address</Label>
                <SlotPicker value={slotAddr} onChange={setSlotAddr} placeholder="Search slot..." data-testid="label-slot-input" />
              </div>
              <Button onClick={handleSlotLabel} disabled={loading === 'slot'} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }} data-testid="generate-slot-label">
                <Printer size={14} className="mr-1" /> {loading === 'slot' ? 'Generating...' : 'Generate Slot Label'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pallet Label */}
        <TabsContent value="pallet">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">A4 Pallet Label</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">Colour header, pallet ref, items with batch/heat, slot chip, label version, QR 100x100mm</p>
              <div>
                <Label className="text-xs">Pallet Ref</Label>
                <Input value={palletRef} onChange={e => setPalletRef(e.target.value)} placeholder="SFX-0001" className="h-9 text-sm font-mono" data-testid="label-pallet-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handlePalletLabel} disabled={!!loading} className="flex-1 h-10" style={{ backgroundColor: '#1a2fce' }} data-testid="generate-pallet-label">
                  <Printer size={14} className="mr-1" /> Print Label
                </Button>
                <Button onClick={handleReprintPallet} disabled={!!loading} variant="outline" className="flex-1 h-10" data-testid="reprint-pallet-label">
                  <Printer size={14} className="mr-1" /> Reprint (v+1)
                </Button>
              </div>
              <Button onClick={handleAllBoxLabels} disabled={!!loading} variant="outline" className="w-full h-9 text-xs" data-testid="print-all-boxes">
                <Box size={12} className="mr-1" /> Print All Box Labels for This Pallet
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Box Label */}
        <TabsContent value="box">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Box/Carton Label (150x100mm)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">Category colour stripe, box ref, item details, batch/heat, QR code</p>
              <div>
                <Label className="text-xs">Box Ref</Label>
                <Input value={boxRef} onChange={e => setBoxRef(e.target.value)} placeholder="SFX-0001-B01" className="h-9 text-sm font-mono" />
              </div>
              <Button onClick={async () => {
                if (!boxRef) return toast.error('Enter box ref');
                setLoading('box');
                try {
                  const res = await getBoxLabel(boxRef.toUpperCase());
                  openLabel(res.data.url);
                  toast.success('Box label generated');
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                finally { setLoading(''); }
              }} disabled={loading === 'box'} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }}>
                <Printer size={14} className="mr-1" /> Generate Box Label
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Companion Board */}
        <TabsContent value="companion">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Companion Board (A4)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">Rack identity header, 60x60mm QR per upper level (L4/L5/L6)</p>
              <div>
                <Label className="text-xs">Rack Code</Label>
                <Input value={rackCode} onChange={e => setRackCode(e.target.value)} placeholder="A05" className="h-9 text-sm font-mono" data-testid="label-rack-input" />
              </div>
              <Button onClick={handleCompanionBoard} disabled={loading === 'companion'} className="w-full h-10" style={{ backgroundColor: '#1a2fce' }} data-testid="generate-companion">
                <Printer size={14} className="mr-1" /> Generate Companion Board
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Challan PDF */}
        <TabsContent value="challan">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Dispatch Challan PDF (A4)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-gray-500">Company header, DC-XXXXX reference, item table, signature areas</p>
              <div>
                <Label className="text-xs">Challan Reference</Label>
                <Input value={challanRef} onChange={e => setChallanRef(e.target.value)} placeholder="DC-00001" className="h-9 text-sm font-mono" data-testid="label-challan-input" />
              </div>
              <Button onClick={handleChallanPDF} disabled={loading === 'challan'} className="w-full h-10" style={{ backgroundColor: '#d97706' }} data-testid="generate-challan-pdf">
                <FileText size={14} className="mr-1" /> Generate Challan PDF
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
